import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreStag } from '~/stores/client/stag';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import type { IBannerRules } from '~/types/banners';

export const findIfPassesByCurrency = (includedCurrencies: string, excludedCurrenncies: string) => {
  const isInIncludedCurrencies = includedCurrencies
    ? includedCurrencies
        .split(',')
        .some((s) => s.toLowerCase() === useStoreRoot().userCurrency.toLowerCase())
    : true;

  const isInExcludedCurrencies = excludedCurrenncies
    ? excludedCurrenncies
        .split(',')
        .some((s) => s.toLowerCase() === useStoreRoot().userCurrency.toLowerCase())
    : false;

  return isInIncludedCurrencies && !isInExcludedCurrencies;
};

export const findIfPassesByStag = (includedStags: string, excludedStags: string) => {
  const isInIncludedStags = includedStags
    ? includedStags
        .split(',')
        .some((s) => s.toLowerCase() === useStoreStag().currentPureStag.toLowerCase())
    : true;

  const isInExcludedStags = excludedStags
    ? excludedStags
        .split(',')
        .some((s) => s.toLowerCase() === useStoreStag().currentPureStag.toLowerCase())
    : false;

  return isInIncludedStags && !isInExcludedStags;
};

export const findIfPassesByGroups = (includedGroups: string, excludedGroups: string) => {
  const isInIncludedGroups = includedGroups
    ? includedGroups.split(',').some((g) => useStoreSsPlayer().playerGroupIds.includes(g))
    : true;

  const isInExcludedGroups = excludedGroups
    ? excludedGroups.split(',').some((g) => useStoreSsPlayer().playerGroupIds.includes(g))
    : false;

  return isInIncludedGroups && !isInExcludedGroups;
};

export const findIfPassesByCountry = (includedCountries: string, excludedCountries: string) => {
  const isInIncludedCountries = includedCountries
    ? includedCountries
        .split(',')
        .some((c) => c.toLowerCase() === useStoreSsPlayer().playerCountry.toLowerCase())
    : true;

  const isInExcludedCountries = excludedCountries
    ? excludedCountries
        .split(',')
        .some((c) => c.toLowerCase() === useStoreSsPlayer().playerCountry.toLowerCase())
    : false;

  return isInIncludedCountries && !isInExcludedCountries;
};

export const findIfPassesByUsers = (usersId: string) => {
  const isInUsers = usersId
    ? usersId
        .replace(/\n/g, '')
        .split(',')
        .some((u) => !!u && useStoreSsPlayer().playerId === u.trim())
    : true;

  return isInUsers;
};

export const processBannerRules = (bannerRules: IBannerRules[]) => {
  const storeSsPlayer = useStoreSsPlayer();
  const storeRoot = useStoreRoot();
  const storeStag = useStoreStag();
  const day = new Date().getDay().toString();
  let result = false;

  for (const rule of bannerRules) {
    // show for authorised check
    if (storeSsPlayer.isPlayerAuthorised && !rule?.showForAuthorized) {
      continue;
    }
    // show for guest check
    if (!storeSsPlayer.isPlayerAuthorised && !rule?.showForGuests) {
      continue;
    }
    // show for included currencies check
    if (
      rule?.includedCurrencies &&
      (!storeRoot.userCurrency ||
        !rule.includedCurrencies.split(',').some((c) => c.toUpperCase() === storeRoot.userCurrency))
    ) {
      continue;
    }
    // hide for excluded currencies check
    if (
      rule?.excludedCurrencies &&
      storeRoot.userCurrency &&
      rule.excludedCurrencies.split(',').some((c) => c.toUpperCase() === storeRoot.userCurrency)
    ) {
      continue;
    }
    if (
      rule?.includedCountries &&
      (!storeSsPlayer.playerCountry ||
        !rule.includedCountries.split(',').includes(storeSsPlayer.playerCountry))
    ) {
      continue;
    }
    if (
      rule?.excludedCountries &&
      storeSsPlayer.playerCountry &&
      rule.excludedCountries.split(',').includes(storeSsPlayer.playerCountry)
    ) {
      continue;
    }
    if (
      rule?.includedGroups &&
      !rule.includedGroups.split(',').some((g) => storeSsPlayer.playerGroupIds.includes(g)) &&
      !rule?.showForGuests
    ) {
      continue;
    }
    if (
      rule?.excludedGroups &&
      rule.excludedGroups
        .split(',')
        .map((g) => g.trim().toLowerCase())
        .some((g) =>
          storeSsPlayer.playerGroupIds.map((id) => id.trim().toLowerCase()).includes(g)
        ) &&
      !rule?.showForGuests
    ) {
      continue;
    }
    if (
      rule?.excludedGroups &&
      rule.excludedGroups
        .split(',')
        .map((g) => g.trim().toLowerCase())
        .some((g) =>
          storeSsPlayer.playerGroupIds.map((id) => id.trim().toLowerCase()).includes(g)
        ) &&
      rule?.showForGuests !== false
    ) {
      continue;
    }
    if (
      rule?.includedStags &&
      (!storeStag.wasRegisteredByPureStag ||
        !rule.includedStags.split(',').includes(storeStag.wasRegisteredByPureStag))
    ) {
      continue;
    }
    if (
      rule?.excludedStags &&
      storeStag.wasRegisteredByPureStag &&
      rule.excludedStags.split(',').includes(storeStag.wasRegisteredByPureStag)
    ) {
      continue;
    }
    if (rule?.includedDays && !rule.includedDays.split(',').includes(day)) {
      continue;
    }
    if (rule?.excludedDays && rule.excludedDays.split(',').includes(day)) {
      continue;
    }
    // stop the loop and show this banner
    result = true;
    break;
  }

  return result;
};

export const filterDateActiveItem = (item: {
  startDate?: Date | null;
  finishDate?: Date | null;
}): boolean => {
  if (item.startDate && new Date(item.startDate).getTime() > Date.now()) {
    return false;
  }

  if (item.finishDate && new Date(item.finishDate).getTime() < Date.now()) {
    return false;
  }

  return true;
};
